import jwt_decode from "jwt-decode";
import { getCookie } from "../api/axios";
import {
    GroupSpecificConfigPermission,
    LayerSpecificConfigPermission,
    FieldSpecificConfigPermission,
    ConfigLayerPermission,
} from "../permissionUtils";

/**
 * @typedef {Object} AllPermissions
 * @property {GroupSpecificConfigPermission | []} group
 * @property {LayerSpecificConfigPermission | []} layer
 * @property {FieldSpecificConfigPermission | []} field
 */

/**
 * Get all permissions for all roles in configuration file
 * @param {Object} action Redux action
 * @returns {AllPermissions}
 */
export const setPermissions = (action) => {
    const layers = action.payload.Site.Map.Layers;
    const [fieldPermissions, layerPermissions, groupPermissions] = getPermissions(layers);
    return {
        group: groupPermissions,
        layer: layerPermissions,
        field: fieldPermissions,
    };
};

/**
 * Recursive function for grabbing permissions
 * @param {Object} l Layer node from configuration file (can be group or layer)
 * @returns {[FieldSpecificConfigPermission[] | [],
 * LayerSpecificConfigPermission[] | [],
 * GroupSpecificConfigPermission[] | []]}
 */
const getPermissions = (l) => {
    const fieldPermissions = [];
    const layerPermissions = [];
    const groupPermissions = [];

    makeArray(l.Layer).forEach((l) => {
        if (l.Type === "Group") {
            const currentGroupPermissions = getGroupPermissions(l);
            groupPermissions.push(...currentGroupPermissions);
            const [fieldPerms, layerPerms, groupPerms] = getPermissions(
                l.SubLayers,
                l.DisplayName
            );
            fieldPermissions.push(...fieldPerms);
            layerPermissions.push(...layerPerms);
            groupPermissions.push(...groupPerms);
        } else if (
            ["GeoJson", "ArcGISImageService", "ArcGISMapService", "WMS"].includes(l.Type)
        ) {
            const [fieldPerms, layerPerms] = getGeoJsonAndFieldPermissions(l);
            if (layerPerms.length > 0) {
                layerPermissions.push({
                    layerId: l.Id,
                    permissions: layerPerms,
                });
            }
            fieldPermissions.push(...fieldPerms);
        } else {
            console.log("Layer type -", l.Type, "- unaccounted for");
        }
    });
    return [fieldPermissions, layerPermissions, groupPermissions];
};

/**
 * Get group level permissions
 * @param {Object} group
 * @returns {GroupSpecificConfigPermission}
 */
const getGroupPermissions = (group) => {
    const groupPermissions = [];
    if (group.SubLayers.Permissions) {
        let gPerm = group.SubLayers.Permissions.Permission;
        groupPermissions.push({
            groupId: group.Name,
            permissions: makeArray(gPerm),
        });
    }
    return groupPermissions;
};

/**
 * Get field level permissions
 * @param {arrayc | Object} fields Fields from configuration file
 * @param {string} layerId Id of layer from configuration file
 * @returns {FieldSpecificConfigPermission[] | []}
 */
const getFieldPermissions = (fields, layerId) => {
    const fieldPermissions = [];
    makeArray(fields).forEach((f) => {
        if (f.Permissions?.Permission) {
            const perm = makeArray(f.Permissions.Permission);
            const fieldPerm = { layerId: layerId, fieldId: f.Name, permissions: perm };
            fieldPermissions.push(fieldPerm);
        }
    });
    return fieldPermissions;
};

/**
 * Get geojson and field level permissions
 * @param {Object} layer Layer from configuration file
 * @returns {[FieldSpecificConfigPermission[] | [], ConfigLayerPermission[] | []]}
 */
const getGeoJsonAndFieldPermissions = (layer) => {
    const layerPermissions = [];
    const fieldPermissions = [];
    if (layer.Permissions) {
        const perm = makeArray(layer.Permissions.Permission);
        layerPermissions.push(...perm);
    }
    if (layer.Fields?.Field) {
        const fieldPerms = getFieldPermissions(layer.Fields.Field, layer.Id);
        fieldPermissions.push(...fieldPerms);
    }
    return [fieldPermissions, layerPermissions];
};

// takes array or object and returns array
export const makeArray = (inData) => {
    return Array.isArray(inData) ? inData : [inData];
};

// Save layer config data to state
export const setLayerConfig = (action) => {
    // get config layers object
    const layers = action.payload.Site.Map.Layers;
    makeArray(layers.Layer).forEach((layer) => {
        if (layer.Type === "Group") {
            layer.SubLayers = layer.SubLayers.Layer;
        }
    });
    // // Shape data to have same structure as layers in sidebar
    const layerArray = Object.values(layers).flatMap((arr) => arr);
    return layerArray;
};

export const setStaticBaseMaps = (action) => {
    if (action.payload.Site.BaseMaps) {
        return action.payload.Site.BaseMaps;
    }
};

const recursivelyCheckForLayers = (layer) => {
    const vectorLayers = [];
    if (layer.hasOwnProperty("Id")) {
        vectorLayers.push(layer);
    } else if (layer.hasOwnProperty("SubLayers")) {
        const subLayers = makeArray(layer.SubLayers);
        subLayers.forEach((subLayer) => {
            vectorLayers.push(...recursivelyCheckForLayers(subLayer));
        });
    }
    return vectorLayers;
};

export const getGroupLayers = (layers) => {
    const groupLayers = [];
    layers.forEach((l) => {
        if (l.SubLayers) {
            groupLayers.push({ DisplayName: l.DisplayName, Visible: l.Visible });
        }
    });
    return groupLayers;
};

// Sort layers by Vector (GeoJson) and BaseMaps (multiple formates)
export const sortLayers = (layers) => {
    const baseMaps = [];
    const vectorLayers = [];
    layers.forEach((layer) => {
        if (layer.IsBaseMap) {
            baseMaps.push(layer);
        } else {
            const vectorLayer = recursivelyCheckForLayers(layer);
            vectorLayers.push(...vectorLayer);
        }
    });
    return [baseMaps, vectorLayers];
};

export const createDynamicState = (layers) => {
    const dynamicProperties = [];
    layers.forEach((layer) => {
        if (layer.Snappable) {
            const snappable = layer.SnappingEnabled && layer.Visible ? true : false;
            const properties = {
                DisplayName: layer.DisplayName,
                Id: layer.Id ?? layer.DisplayName,
                Visible: layer.Visible,
                Snappable: snappable,
                Opacity: layer.Opacity ?? 1,
            };
            dynamicProperties.push(properties);
        } else {
            const properties = {
                Id: layer.Id ?? layer.DisplayName,
                Visible: layer.Visible,
                Opacity: layer.Opacity ?? 1,
            };
            dynamicProperties.push(properties);
        }
    });
    return dynamicProperties;
};

export const getBaseMapProperties = (baseMaps) => {
    let properties = [];
    baseMaps.forEach((map) => {
        properties.push({ Id: map.DisplayName, Visible: map.Visible });
    });
    return properties;
};

export const setSnappableLayers = (layers) => {
    let snapLayers = [];
    layers.forEach((layer) => {
        if (layer.Snappable === true) {
            snapLayers.push({
                Id: layer.Id,
                Snappable: layer.SnappingEnabled,
                DisplayName: layer.DisplayName,
            });
        }
    });
    return snapLayers;
};

const retrieveMapProperties = (layers, tokenRoles) => {
    const properties = {
        editable: [],
        reports: [],
        tempAdd: [],
        tempEdit: [],
        tempDelete: [],
        tempEditShape: [],
        tempEditAttachment: [],
        searchableFields: [],
    };
    makeArray(layers).forEach((layerElement) => {
        if (layerElement.Type === "Group") {
            const nestedProperties = retrieveMapProperties(
                layerElement.SubLayers,
                tokenRoles
            );
            Object.keys(properties).forEach((p) => {
                properties[p].push(...nestedProperties[p]);
            });
        } else {
            if (layerElement.Searchable) {
                if (layerElement.Fields) {
                    const fields = makeArray(layerElement.Fields.Field);
                    fields.forEach((fieldElement) => {
                        if (fieldElement.Searchable) {
                            properties.searchableFields.push({
                                DisplayName: layerElement.DisplayName,
                                LayerID: layerElement.Id,
                                FeatureDescription: layerElement.FeatureDescription,
                                FieldName: fieldElement.Name,
                                FieldDisplayName: (fieldElement.DisplayName ??=
                                    fieldElement.Name),
                                Checked: true,
                            });
                        }
                    });
                }
            }
            if (layerElement.Editable) {
                if (layerElement.Permissions) {
                    let perm = makeArray(layerElement.Permissions.Permission);
                    tokenRoles.forEach((tRoles) => {
                        var permResults = perm.find((obj) => {
                            return obj.Value === tRoles;
                        });
                        if (permResults) {
                            if (permResults.CreateFeature === true) {
                                properties.tempAdd.push(layerElement);
                            }
                            if (permResults.UpdateFeature === true) {
                                properties.tempEdit.push(layerElement);
                            }
                            if (permResults.DeleteFeature === true) {
                                properties.tempDelete.push(layerElement);
                            }
                            if (permResults.GeometryEditable === true) {
                                properties.tempEditShape.push(layerElement);
                            }
                            if (permResults.EditAttachment === true) {
                                properties.tempEditAttachment.push(layerElement);
                            }
                        }
                    });
                }
            }
            if (layerElement.Reports) {
                // Can read by default
                let readPermission = true;
                if (layerElement.Permissions?.Permission) {
                    const permissions = makeArray(layerElement.Permissions.Permission);
                    // Filter permissions assinged to layer by token roles
                    const filteredPermissions = permissions.filter((p) =>
                        tokenRoles.includes(p.Value)
                    );
                    // If any role does not have read permission, then readPermission is false
                    filteredPermissions.forEach((p) => {
                        if (p.Read === false) readPermission = false;
                    });
                }
                if (readPermission) {
                    makeArray(layerElement.Reports.LayerTemplateReport).forEach(
                        (reportElement) => {
                            reportElement.LayerID = layerElement.Id;
                            const report = { ...reportElement, Type: "LayerReport" };
                            properties.reports.push(report);
                        }
                    );
                }
            }
        }
    });
    return properties;
};

export const setMapProperties = (action) => {
    const data = action.payload.Site;

    const token = getCookie("ast");
    let tokenRoles = ["GUEST"];
    if (token) {
        tokenRoles = jwt_decode(token).realm_access.roles;
    }
    const layers = data.Map.Layers;
    const properties = retrieveMapProperties(layers.Layer, tokenRoles);
    if (data.SiteReports) {
        const siteReports = makeArray(data.SiteReports.SiteReport).map((o) => ({
            ...o,
            Type: "SiteReport",
        }));
        properties.reports.push(...siteReports);
    }

    const mapConfig = {
        DisplayName: data.DisplayName,
        ID: data.ID,
        map: {
            fullExtent: data.Map.FullExtent,
            initialExtent: data.Map.InitialExtent,
            projection: data.WKID,
            units: data.Units,
        },
        Permissions: data.Permissions.Allow ?? ["GUEST"],
        SearchFields: properties.searchableFields,
        GeocodingEndpoints: data.GeocodingEndpoints ?? null,
        Reports: properties.reports,
        AddLayers: properties.tempAdd,
        EditLayers: properties.tempEdit,
        DeleteLayers: properties.tempDelete,
        EditShape: properties.tempEditShape,
        EditAttachment: properties.tempEditAttachment,
    };
    return mapConfig;
};

export const setPrintTemplates = (action) => {
    const templates = action.payload.Site.PrintTemplates.PrintTemplate;
    const printTemplates = {};
    // if one print template
    if (Array.isArray(templates) === false) {
        printTemplates.defaultTemplate = templates;
    } else {
        const defaultTemplate = templates.find((t) => t.IsDefault === true);
        printTemplates.defaultTemplate = defaultTemplate;
        printTemplates.templates = templates;
    }
    return printTemplates;
};

export const setThirdPartyMaps = (action) => {
    const maps = action.payload?.Site?.ThirdPartyMaps;
    if (maps) {
        return maps;
    } else {
        return { GoogleStreetView: { Enabled: true } };
    }
};
